import dlv from 'dlv';
import { router } from 'expo-router';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Platform, View } from 'react-native';

import { IconDelete, IconRedeem, IconSettings, Pressable } from '@fhs/ui';
import { HStack, Icon, Image, Text, VStack } from '@fhs-legacy/universal-components';
import { IBaseProps } from '@rbi-ctg/frontend';
import RadioIndicator from 'components/radio-indicator';
import useDialogModal from 'hooks/use-dialog-modal';
import { CardTypes, ICredit, IPaymentMethod } from 'state/payment/types';
import { theme } from 'styles/configure-theme';
import { mapPaymentMethodCardTypes } from 'utils/payment/map-payment-method-card-type';
import { routes } from 'utils/routing';

import CashMethod from './cash-method';
import { CONFIRM_DELETE_PAYMENT_METHOD_ID } from './constants';
import { cardIconRenderer } from './credit-card-icon';
import { GiftCardMethod } from './gift-card-method';
import PayPalMethod from './paypal-method';
import PrepaidCardIcon from './prepaid-card-icon';
import PrepaidMethodBalance from './prepaid-method-balance';
import { MethodContainer, MethodType, MethodTypeInnerWrapper, MethodTypeWrapper } from './styled';
import { UnverifiedMethod } from './unverified-method';

export interface IPaymentMethodOptionProps extends IBaseProps {
  method: IPaymentMethod;
  selected?: boolean;
  onPress?: (fdAccountId: string) => void;
  showDefault?: boolean;
  removable?: boolean;
  readonly?: boolean;
  disablePress?: boolean;
  deletePaymentMethod?: (fdAccountId: string) => Promise<void>;
  hideDeleteIcon?: boolean;
  disableHover?: boolean;
  disableMethod?: boolean;
  hideBorder?: boolean;
  ariaLabelledby?: string;
  revaultRequired?: boolean;
  fromCheckout?: boolean;
  isDefaultPaymentMethod?: boolean;
  fullWidthDescription?: boolean;
  hasError?: boolean;
  showGiftCardBalance?: boolean;
}

function PaymentMethodOption({
  selected,
  method,
  onPress,
  showDefault,
  removable,
  disablePress = false,
  deletePaymentMethod,
  disableHover = false,
  disableMethod = false,
  hideDeleteIcon = false,
  hideBorder = false,
  ariaLabelledby = 'payment-method-loads-with payment-method-label payment-method-card payment-method-alias payment-method-expirationDate',
  revaultRequired,
  fromCheckout = false,
  isDefaultPaymentMethod = false,
  fullWidthDescription = true,
  hasError = false,
  showGiftCardBalance = false,
}: IPaymentMethodOptionProps) {
  const { formatMessage } = useIntl();

  const accountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';
  const accountAlias = dlv(method, 'credit.alias', dlv(method, 'prepaid.alias'));
  const isGiftCard = Boolean(method.giftCard);
  const showDeleteButton = !hideDeleteIcon && !isGiftCard;
  const showGiftCardOptionButton = !hideDeleteIcon && isGiftCard;
  const enableDeleteButton = removable && !disablePress;
  const textColor = disableMethod ? theme.token('text-disabled') : theme.token('text-default');

  const removePaymentMethodAccessibilityLabel = accountAlias
    ? `${formatMessage({ id: 'removePaymentMethod' })}, ${formatMessage({ id: 'endsIn' })} ${accountAlias}`
    : formatMessage({ id: 'removePaymentMethod' });

  const paymentMethodSummaryForA11y = useMemo(() => {
    if (method.credit?.alias) {
      return `${formatMessage({ id: 'endsIn' })} ${method.credit.alias}`;
    }
    if (method.prepaid?.alias) {
      return `${formatMessage({ id: 'endsIn' })} ${method.prepaid.alias}`;
    }
    if (method.giftCard?.last4) {
      return `Gift card, ${formatMessage({ id: 'endsIn' })} ${method.giftCard.last4}`;
    }
    if (method.paypal) {
      return 'PayPal';
    }
    if (method.cash) {
      return formatMessage({ id: 'cash' });
    }
    return formatMessage({ id: 'paymentMethod' });
  }, [method, formatMessage]);

  const paymentRowAccessibilityLabel = useMemo(() => {
    if (!onPress || !showDefault) {
      return undefined;
    }
    const isCurrentDefault = isDefaultPaymentMethod || selected;
    const defaultPart = isCurrentDefault
      ? formatMessage({
          id: 'paymentMethodIsDefaultAccessibility',
          defaultMessage: 'Default payment method',
        })
      : formatMessage({
          id: 'paymentMethodSetAsDefaultAccessibility',
          defaultMessage: 'Double tap to set as default',
        });
    return `${paymentMethodSummaryForA11y}. ${defaultPart}`;
  }, [
    onPress,
    showDefault,
    isDefaultPaymentMethod,
    selected,
    paymentMethodSummaryForA11y,
    formatMessage,
  ]);

  const onDeleteMethod = useCallback(() => {
    deletePaymentMethod?.(accountIdentifier);
  }, [deletePaymentMethod, accountIdentifier]);

  const onClickMethod = useCallback(() => {
    if (disableMethod || disablePress) {
      return;
    }
    if (typeof onPress === 'function') {
      onPress(accountIdentifier);
    }
  }, [disableMethod, disablePress, onPress, accountIdentifier]);

  const [DeletionConfirmationDialog, openDeletionConfirmationDialog] = useDialogModal({
    onConfirm: onDeleteMethod,
    showCancel: true,
    modalAppearanceEventMessage: 'Confirmation: Delete payment method',
  });

  const onClickRevault = useCallback(
    (e: any) => {
      e.stopPropagation();
      router.navigate({ pathname: routes.addCard, params: { accountToDelete: accountIdentifier } });
    },
    [accountIdentifier]
  );

  const navigateToGiftCardsScreen = () => {
    router.navigate(routes.giftCards);
  };

  const formatExpirationDate = (credit: ICredit) => {
    const { expiryYear: expYr, expiryMonth: expMon } = credit || {};
    if (!expYr || !expMon) {
      return;
    }
    const expirationDate = new Date(2000 + parseInt(expYr, 10), parseInt(expMon, 10));
    const currentDate = new Date();
    const isValid = expirationDate.getTime() - currentDate.getTime();

    return `${
      isValid ? formatMessage({ id: 'expire' }) : formatMessage({ id: 'expired' })
    } ${expMon}/${expYr}`;
  };

  const RenderMethodType = () => {
    if (method.credit) {
      const creditCardAlias = (
        <Text color={textColor}>
          {formatMessage({ id: 'endsIn' }).toLowerCase()} {method.credit.alias}
        </Text>
      );
      let isCreditCard;
      let alias;
      let cardType: CardTypes | undefined;
      switch (method.credit.cardType) {
        case CardTypes.APPLE_PAY:
          isCreditCard = false;
          alias = formatMessage({ id: 'applePay' });
          break;
        case CardTypes.GOOGLE_PAY:
          isCreditCard = false;
          alias = formatMessage({ id: 'googlePay' });
          break;
        default:
          cardType = mapPaymentMethodCardTypes(method.credit.cardType);
          isCreditCard = true;
          alias = creditCardAlias;
      }
      return (
        <>
          {fullWidthDescription ? (
            <View
              style={{
                flex: 1,
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {isCreditCard && (
                <MethodType color={textColor} testID="payment-method-card">
                  {cardType || 'CARD'}
                </MethodType>
              )}
              <MethodType color={textColor} textTransform="capitalize">
                {alias}
              </MethodType>
              {isCreditCard && (
                <Text
                  color={textColor}
                  testID="payment-method-expirationDate"
                  textTransform="capitalize"
                >
                  {formatExpirationDate(method.credit)}
                </Text>
              )}
            </View>
          ) : (
            <VStack justifyContent="center">
              <HStack space={2}>
                {isCreditCard && (
                  <MethodType color={textColor} testID="payment-method-card">
                    {cardType || 'CARD'}
                  </MethodType>
                )}
                <MethodType color={textColor} textTransform="capitalize">
                  {alias}
                </MethodType>
              </HStack>
              {isCreditCard && (
                <HStack>
                  <Text
                    color={textColor}
                    testID="payment-method-expirationDate"
                    textTransform="capitalize"
                    fontSize="xs"
                  >
                    {formatExpirationDate(method.credit)}
                  </Text>
                </HStack>
              )}
            </VStack>
          )}
        </>
      );
    }

    if (method.prepaid) {
      return (
        <PrepaidMethodBalance
          method={method}
          isDisabled={disableMethod}
          fullWidthDescription={fullWidthDescription}
        />
      );
    }

    if (method.cash) {
      return <CashMethod isDisabled={disableMethod} />;
    }

    if (method.paypal) {
      return <PayPalMethod isDisabled={disableMethod} />;
    }

    if (method.giftCard) {
      return (
        <GiftCardMethod
          method={method}
          isDisabled={disableMethod}
          hasError={hasError}
          showBalance={showGiftCardBalance}
        />
      );
    }

    return null;
  };

  return (
    <>
      <MethodContainer
        disableHover={disableHover}
        disableMethod={disableMethod}
        hideBorder={hideBorder}
        aria-labelledby={ariaLabelledby}
        testID={`payment-method-${accountIdentifier}`}
      >
        <MethodTypeWrapper
          data-private
          onPress={onClickMethod}
          $isClickable={!!onPress}
          disableMethod={disableMethod}
          fromCheckout={fromCheckout}
          testID={`method-type-wrapper-${accountIdentifier}`}
          accessibilityRole="button"
          accessibilityLabel={paymentRowAccessibilityLabel}
          accessibilityState={{
            disabled: disableMethod || disablePress,
            ...(showDefault && onPress
              ? { selected: Boolean(isDefaultPaymentMethod || selected) }
              : {}),
          }}
        >
          <VStack flex={1}>
            <MethodTypeInnerWrapper space={2} testID="payment-method-types">
              <View style={{ alignItems: 'center', marginRight: 8 }}>
                {method.credit && cardIconRenderer(method.credit.cardType)}
                {method.prepaid && <PrepaidCardIcon />}
                {method.cash && <Icon variant="cash" />}
                {method.paypal && (
                  <Image
                    src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_37x23.jpg"
                    alt=""
                    width="32px"
                  />
                )}
                {isGiftCard && <IconRedeem size={24} color={theme.primitives.colors.houseDark} />}
              </View>

              <RenderMethodType />
              {disableMethod && (
                <HStack alignItems="center">
                  <Icon
                    variant="error"
                    size="$5"
                    color={theme.token('text-error')}
                    mr="$1"
                    ml="$2"
                  />
                  <Text color={theme.token('text-error')} variant="copyTwo">
                    {formatMessage({ id: 'paymentMethodNotAvailable' })}
                  </Text>
                </HStack>
              )}
            </MethodTypeInnerWrapper>
            {revaultRequired && <UnverifiedMethod onPress={onClickRevault} />}
          </VStack>
          {showDefault && (
            <RadioIndicator
              accessibilityElementsHidden={Boolean(paymentRowAccessibilityLabel)}
              importantForAccessibility={
                paymentRowAccessibilityLabel ? 'no-hide-descendants' : 'auto'
              }
              {...(paymentRowAccessibilityLabel ? {} : { accessibilityLabel: accountAlias })}
              isSelected={selected || isDefaultPaymentMethod}
              isDisabled={disableMethod || disablePress}
            />
          )}
        </MethodTypeWrapper>
        {showDeleteButton && (
          <Pressable
            onPress={openDeletionConfirmationDialog}
            disabled={!enableDeleteButton}
            disabledStyle={{ opacity: 0.1 }}
            style={{ marginLeft: 12 }}
            testID="remove-card-button"
            accessibilityRole="button"
            accessibilityLabel={removePaymentMethodAccessibilityLabel}
            accessibilityHint={formatMessage({
              id: 'removePaymentMethodAccessibilityHint',
              defaultMessage: 'Opens a confirmation to remove this payment method',
            })}
            accessibilityState={{ disabled: !enableDeleteButton }}
            focusable={Boolean(enableDeleteButton)}
            {...(Platform.OS === 'web' ? ({ tabIndex: enableDeleteButton ? 0 : -1 } as const) : {})}
          >
            <View
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
              {...(Platform.OS === 'web' ? { 'aria-hidden': true } : {})}
            >
              <IconDelete size={24} />
            </View>
          </Pressable>
        )}
        {showGiftCardOptionButton && (
          <Pressable
            onPress={navigateToGiftCardsScreen}
            style={{ marginLeft: 12 }}
            testID="settings-card-button"
            accessibilityRole="button"
            accessibilityLabel={formatMessage({
              id: 'giftCardSettingsButton',
              defaultMessage: 'Gift card settings',
            })}
            accessibilityHint={formatMessage({
              id: 'giftCardSettingsAccessibilityHint',
              defaultMessage: 'Opens gift card management',
            })}
            focusable
            {...(Platform.OS === 'web' ? ({ tabIndex: 0 } as const) : {})}
            disabled={disablePress}
          >
            <View
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
              {...(Platform.OS === 'web' ? { 'aria-hidden': true } : {})}
            >
              <IconSettings size={24} />
            </View>
          </Pressable>
        )}
      </MethodContainer>

      <DeletionConfirmationDialog
        testID="delete-confirmation-dialog"
        heading={formatMessage({ id: 'deletePaymentMethod' })}
        body={formatMessage({ id: CONFIRM_DELETE_PAYMENT_METHOD_ID })}
        modalAppearanceEventMessage="Confirmation: Delete Payment Method"
      />
    </>
  );
}

export default PaymentMethodOption;
